import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Select from "../../components/ui/Select";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Switch from "../../components/ui/Switch";
import {
    createHallRequest,
    getHallsRequest,
    updateHallRequest,
    uploadHallPhotoRequest,
    deleteHallPhotoRequest,
    importPartnerSeanceIntoHallRequest,
    listNashePerformancesRequest,
    createHallFromNashePerformanceRequest,
    importNashePerformanceIntoHallRequest,
} from "../../services/api/event-content.api";
import {
    NashePerformanceItem,
    HallItem,
    HallListResponse,
    HallScheme,
} from "../../services/types/event-content.types";
import HallSchemeEditorModal from "../../components/hall-schemes/HallSchemeEditorModal";
import { getCountriesRequest, getCitiesRequest } from "../../services/api/geo.api";
import { CountryItem, CityItem } from "../../services/types/geo.types";

interface HallFormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    internalName: string;
    addressRu: string;
    addressEn: string;
    addressDe: string;
    addressUa: string;
    descriptionRu: string;
    descriptionEn: string;
    descriptionDe: string;
    descriptionUa: string;
    countryId: string;
    cityId: string;
    zipCode: string;
    scheme: HallScheme | null;
    website: string;
    sort: string;
    seats_count: string;
    isActive: boolean;
    /** ID сеанса Artist Production (вручное поле или значение для импорта). */
    partnerSeanceId: string;
    /** ID мероприятия партнёра Nashe (для импорта схемы зала). */
    nashePerformanceId: string;
    /** Чьё мероприятие: от этого зависят поля партнёра в форме. */
    owner: HallOwner;
}

type HallOwner = "showmust" | "artist_production" | "nashe_de";

const HALL_OWNER_OPTIONS: { value: HallOwner; label: string }[] = [
    { value: "showmust", label: "ShowMust" },
    { value: "artist_production", label: "Artist Production" },
    { value: "nashe_de", label: "NasheDe" },
];

// Владелец зала по сохранённым данным: ИД мероприятия Nashe → NasheDe,
// ИД сеанса Artist Production → Artist Production, иначе — ShowMust.
function detectHallOwner(item: { partner_seance_id?: number | null; nashe_performance_id?: number | null }): HallOwner {
    if (item.nashe_performance_id !== null && item.nashe_performance_id !== undefined) return "nashe_de";
    if (item.partner_seance_id !== null && item.partner_seance_id !== undefined) return "artist_production";
    return "showmust";
}

const initialFormState: HallFormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    internalName: "",
    addressRu: "",
    addressEn: "",
    addressDe: "",
    addressUa: "",
    descriptionRu: "",
    descriptionEn: "",
    descriptionDe: "",
    descriptionUa: "",
    countryId: "",
    cityId: "",
    zipCode: "",
    scheme: null,
    website: "",
    sort: "0",
    seats_count: "0",
    isActive: true,
    partnerSeanceId: "",
    nashePerformanceId: "",
    owner: "showmust",
};

const HallsPage: React.FC = () => {
    const [items, setItems] = useState<HallItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<HallFormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [hallPhotos, setHallPhotos] = useState<string[]>([]);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
    const [isSchemeEditorOpen, setIsSchemeEditorOpen] = useState(false);

    // Создание зала из данных партнёра Artist Production по ID сеанса.
    const [isImportingFromPartner, setIsImportingFromPartner] = useState(false);
    const [importWarnings, setImportWarnings] = useState<string[]>([]);

    // Импорт схемы зала из мероприятия партнёра Nashe.
    const [isImportingFromNashe, setIsImportingFromNashe] = useState(false);
    const [nashePerformances, setNashePerformances] = useState<NashePerformanceItem[]>([]);
    const [isLoadingNashePerformances, setIsLoadingNashePerformances] = useState(false);

    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [cities, setCities] = useState<CityItem[]>([]);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);

    // Фильтр списка залов по городу (независимо от формы).
    const [filterCityId, setFilterCityId] = useState<string>("");
    const [allCities, setAllCities] = useState<CityItem[]>([]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalItems / pageSize)),
        [totalItems, pageSize],
    );

    const normalizeResponse = (response: HallListResponse) => {
        if (Array.isArray(response)) {
            return { items: response, total: response.length };
        }

        return {
            items: response.items ?? [],
            total: response.total_size ?? response.items?.length ?? 0,
        };
    };

    useEffect(() => {
        const preventWindowDrop = (event: DragEvent) => {
            event.preventDefault();
        };

        window.addEventListener("dragover", preventWindowDrop);
        window.addEventListener("drop", preventWindowDrop);

        return () => {
            window.removeEventListener("dragover", preventWindowDrop);
            window.removeEventListener("drop", preventWindowDrop);
        };
    }, []);

    useEffect(() => {
        const loadCountries = async () => {
            setIsLoadingCountries(true);

            try {
                const response = await getCountriesRequest();
                setCountries(response);
            } catch (error) {
                setFormError(error instanceof Error ? error.message : "Не удалось загрузить страны");
            } finally {
                setIsLoadingCountries(false);
            }
        };

        void loadCountries();
    }, []);

    // Загружаем все города один раз — для выпадашки фильтра.
    useEffect(() => {
        const loadAllCities = async () => {
            try {
                const response = await getCitiesRequest();
                setAllCities(response);
            } catch (error) {
                // Молча — фильтр не критичен.
                console.warn("Failed to load cities for filter", error);
            }
        };

        void loadAllCities();
    }, []);

    useEffect(() => {
        if (!formState.countryId) {
            setCities([]);
            return;
        }

        const loadCities = async () => {
            setIsLoadingCities(true);

            try {
                const response = await getCitiesRequest(formState.countryId);
                setCities(response);
            } catch (error) {
                setFormError(error instanceof Error ? error.message : "Не удалось загрузить города");
            } finally {
                setIsLoadingCities(false);
            }
        };

        void loadCities();
    }, [formState.countryId]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getHallsRequest({
                page,
                size: pageSize,
                search: searchValue.trim() || undefined,
                cityId: filterCityId || undefined,
            });

            const normalized = normalizeResponse(response);
            setItems(normalized.items);
            setTotalItems(normalized.total);

            if (selectedId) {
                const selectedItem = normalized.items.find((item) => item.id === selectedId);

                if (!selectedItem) {
                    setSelectedId(null);
                    setFormState(initialFormState);
                    setHallPhotos([]);
                } else {
                    setFormState((prev) => ({
                        ...prev,
                        scheme: selectedItem.scheme ?? prev.scheme ?? null,
                        seats_count: String(selectedItem.seats_count ?? 0),
                    }));
                }
            }
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить залы.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, [page, pageSize, searchValue, filterCityId]);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setHallPhotos([]);
        setFormError(null);
        setSuccessMessage(null);
        setImportWarnings([]);
    };

    const handleImportFromPartnerSeance = async () => {
        setFormError(null);
        setSuccessMessage(null);
        setImportWarnings([]);

        const seanceIdRaw = formState.partnerSeanceId.trim();
        const seanceId = Number(seanceIdRaw);

        if (!seanceIdRaw || !Number.isFinite(seanceId) || seanceId <= 0) {
            setFormError("Укажите корректный ID сеанса Artist Production (целое число).");
            return;
        }

        // Кнопка работает ТОЛЬКО для уже сохранённого зала — импортирует данные
        // НАПРЯМУЮ в него, полностью заменяя текущую схему (зоны/места).
        if (!selectedId) {
            setFormError(
                "Сначала сохраните зал (укажите название и город), затем импортируйте схему из API партнёра.",
            );
            return;
        }

        const confirmed = window.confirm(
            "Это полностью заменит текущую схему этого зала (зоны и места) данными из сеанса Artist Production. Все ручные правки схемы этого зала будут потеряны. Продолжить?",
        );
        if (!confirmed) {
            return;
        }

        setIsImportingFromPartner(true);

        try {
            const response = await importPartnerSeanceIntoHallRequest(selectedId, seanceId);

            setSuccessMessage("Схема зала обновлена данными Artist Production.");
            setImportWarnings(response.warnings ?? []);
            await loadData();
            handleSelect(response.hall);
        } catch (error) {
            setFormError(
                error instanceof Error
                    ? error.message
                    : "Не удалось импортировать схему из данных партнёра.",
            );
        } finally {
            setIsImportingFromPartner(false);
        }
    };

    const handleLoadNashePerformances = async () => {
        setFormError(null);
        setIsLoadingNashePerformances(true);
        try {
            const response = await listNashePerformancesRequest();
            setNashePerformances(response.performances ?? []);
            if (!response.performances?.length) {
                setFormError("Nashe не вернул ни одного мероприятия для нашего ключа.");
            }
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Не удалось загрузить список мероприятий Nashe.",
            );
        } finally {
            setIsLoadingNashePerformances(false);
        }
    };

    const handleImportFromNashe = async () => {
        setFormError(null);
        setSuccessMessage(null);
        setImportWarnings([]);

        const performanceIdRaw = formState.nashePerformanceId.trim();
        const performanceId = Number(performanceIdRaw);
        if (!performanceIdRaw || !Number.isInteger(performanceId) || performanceId <= 0) {
            setFormError("Укажите корректный ID мероприятия Nashe (целое число).");
            return;
        }

        const confirmed = window.confirm(
            selectedId
                ? "Это полностью заменит текущую схему этого зала (категории и места) данными мероприятия Nashe. Все ручные правки схемы будут потеряны. Продолжить?"
                : "Будет создан новый зал по схеме зала мероприятия Nashe (название и адрес — из данных площадки). Продолжить?",
        );
        if (!confirmed) {
            return;
        }

        setIsImportingFromNashe(true);
        try {
            const response = selectedId
                ? await importNashePerformanceIntoHallRequest(selectedId, performanceId)
                : await createHallFromNashePerformanceRequest(performanceId);

            setSuccessMessage(
                selectedId ? "Схема зала обновлена данными Nashe." : "Зал создан по данным Nashe.",
            );
            setImportWarnings(response.warnings ?? []);
            await loadData();
            handleSelect(response.hall);
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Не удалось импортировать схему из данных Nashe.",
            );
        } finally {
            setIsImportingFromNashe(false);
        }
    };

    const handleSelect = (item: HallItem) => {
        setSelectedId(item.id);
        setFormState({
            nameRu: item.name_ru ?? "",
            nameEn: item.name_en ?? "",
            nameDe: item.name_de ?? "",
            nameUa: item.name_ua ?? "",
            internalName: item.internal_name ?? "",
            addressRu: item.address_ru ?? "",
            addressEn: item.address_en ?? "",
            addressDe: item.address_de ?? "",
            addressUa: item.address_ua ?? "",
            descriptionRu: item.description_ru ?? "",
            descriptionEn: item.description_en ?? "",
            descriptionDe: item.description_de ?? "",
            descriptionUa: item.description_ua ?? "",
            countryId: item.country_id ?? "",
            cityId: item.city_id ?? "",
            zipCode: item.zip_code ?? "",
            scheme: item.scheme ?? null,
            website: item.website ?? "",
            sort: String(item.sort ?? 0),
            seats_count: String(item.seats_count ?? 0),
            isActive: item.is_active,
            partnerSeanceId:
                item.partner_seance_id !== null && item.partner_seance_id !== undefined
                    ? String(item.partner_seance_id)
                    : "",
            nashePerformanceId:
                item.nashe_performance_id !== null && item.nashe_performance_id !== undefined
                    ? String(item.nashe_performance_id)
                    : "",
            owner: detectHallOwner(item),
        });
        setHallPhotos(item.photos ?? []);
        setFormError(null);
        setSuccessMessage(null);
        setImportWarnings([]);
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(1);
        setSearchValue(searchInputValue);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Название зала должно быть заполнено на трёх языках.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                name_ru: formState.nameRu.trim(),
                name_en: formState.nameEn.trim(),
                name_de: formState.nameDe.trim(),
                name_ua: formState.nameUa.trim() || null,
                internal_name: formState.internalName.trim() || null,
                address_ru: formState.addressRu.trim() || null,
                address_en: formState.addressEn.trim() || null,
                address_de: formState.addressDe.trim() || null,
                address_ua: formState.addressUa.trim() || null,
                description_ru: formState.descriptionRu.trim() || null,
                description_en: formState.descriptionEn.trim() || null,
                description_de: formState.descriptionDe.trim() || null,
                description_ua: formState.descriptionUa.trim() || null,
                country_id: formState.countryId || null,
                city_id: formState.cityId || null,
                zip_code: formState.zipCode.trim() || null,
                website: formState.website.trim() || null,
                sort: Number(formState.sort || 0),
                photos: hallPhotos,
                is_active: formState.isActive,
                // Сохраняем ИД только того партнёра, который выбран в «Чьё мероприятие»;
                // для ShowMust оба ИД очищаются.
                partner_seance_id:
                    formState.owner === "artist_production" && formState.partnerSeanceId.trim()
                        ? Number(formState.partnerSeanceId.trim())
                        : null,
                nashe_performance_id:
                    formState.owner === "nashe_de" && formState.nashePerformanceId.trim()
                        ? Number(formState.nashePerformanceId.trim())
                        : null,
            };

            const savedItem = selectedId
                ? await updateHallRequest(selectedId, payload)
                : await createHallRequest(payload);

            setSuccessMessage(selectedId ? "Зал обновлён." : "Зал создан.");

            if (selectedId) {
                setItems((prev) => prev.map((item) => (item.id === savedItem.id ? savedItem : item)));
                handleSelect(savedItem);
            } else {
                setPage(1);
                setSearchValue("");
                setSearchInputValue("");
                setSelectedId(savedItem.id);
                await loadData();
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения зала");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUploadHallPhotos = async (files: FileList | null) => {
        if (!files || !selectedId) {
            return;
        }

        setIsUploadingPhotos(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            let nextPhotos = [...hallPhotos];

            for (const file of Array.from(files)) {
                const response = await uploadHallPhotoRequest(selectedId, file);
                const uploadedHall = Array.isArray(response) ? response[0] : response?.data ?? response;
                const uploadedPhotos = uploadedHall?.photos ?? nextPhotos;
                nextPhotos = uploadedPhotos;
            }

            setHallPhotos(nextPhotos);

            setItems((prev) =>
                prev.map((item) =>
                    item.id === selectedId
                        ? {
                            ...item,
                            photos: nextPhotos,
                        }
                        : item,
                ),
            );

            setSuccessMessage("Фотографии успешно загружены.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить фотографии");
        } finally {
            setIsUploadingPhotos(false);
        }
    };

    const handleDeleteHallPhoto = async (photoUrl: string) => {
        if (!selectedId) {
            return;
        }

        setIsDeletingPhoto(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            await deleteHallPhotoRequest(selectedId, photoUrl);

            const nextPhotos = hallPhotos.filter((item) => item !== photoUrl);
            setHallPhotos(nextPhotos);

            await updateHallRequest(selectedId, {
                photos: nextPhotos,
            });

            setItems((prev) =>
                prev.map((item) =>
                    item.id === selectedId
                        ? {
                            ...item,
                            photos: nextPhotos,
                        }
                        : item,
                ),
            );

            setSuccessMessage("Фотография удалена.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить фотографию");
        } finally {
            setIsDeletingPhoto(false);
        }
    };

    const moveHallPhoto = async (fromIndex: number, toIndex: number) => {
        if (!selectedId || toIndex < 0 || toIndex >= hallPhotos.length) {
            return;
        }

        const nextPhotos = [...hallPhotos];
        const [movedPhoto] = nextPhotos.splice(fromIndex, 1);
        nextPhotos.splice(toIndex, 0, movedPhoto);

        setHallPhotos(nextPhotos);

        try {
            await updateHallRequest(selectedId, {
                photos: nextPhotos,
            });

            setItems((prev) =>
                prev.map((item) =>
                    item.id === selectedId
                        ? {
                            ...item,
                            photos: nextPhotos,
                        }
                        : item,
                ),
            );
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось обновить сортировку фото");
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Залы"
                description="Справочник залов с многоязычными адресами, городами, описаниями и сортировкой."
                actions={<Button onClick={resetForm}>Новый зал</Button>}
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список залов</h3>
                            <p className="card-text">Список залов доступных для событий</p>
                        </div>

                        <form className="section-toolbar-controls" onSubmit={handleSearchSubmit}>
                            {/*<Input*/}
                            {/*    type="text"*/}
                            {/*    placeholder="Поиск по названию"*/}
                            {/*    value={searchInputValue}*/}
                            {/*    onChange={(event) => setSearchInputValue(event.target.value)}*/}
                            {/*/>*/}
                            <Select
                                placeholder="Все города"
                                value={filterCityId}
                                onChange={(event) => {
                                    setPage(1);
                                    setFilterCityId(event.target.value);
                                }}
                                options={allCities.map((city) => ({
                                    value: city.id,
                                    label: city.name_ru,
                                }))}
                            />
                        </form>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Залы не найдены</h3>
                            <p className="empty-block-text">Создайте первый зал или измените поисковый запрос.</p>
                        </div>
                    ) : (
                        <>
                            <div className="artist-list-grid">
                                {items.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`artist-list-item artist-list-item-compact ${item.id === selectedId ? "artist-list-item-active" : ""}`}
                                        onClick={() => handleSelect(item)}
                                    >
                                        <div className="artist-list-content">
                                            <span className="artist-list-title">{item.internal_name?.trim() || item.name_ru}</span>
                                            <span className="artist-list-subtitle">
                                                {item.city?.name_ru}
                                                {item.seats_count > 0 ? <> · {item.seats_count} мест</> : null}
                                            </span>
                                        </div>

                                        <div className="inline-actions">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleSelect(item);
                                                    setIsSchemeEditorOpen(true);
                                                }}
                                            >
                                                Редактировать схему
                                            </Button>

                                            <span
                                                className={`status-dot ${item.is_active ? "status-dot-active" : "status-dot-inactive"}`}
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="pagination-row">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                >
                                    Назад
                                </Button>

                                <span className="pagination-text">
                                    Страница {page} из {totalPages}
                                </span>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                >
                                    Вперед
                                </Button>
                            </div>
                        </>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">{selectedId ? "Редактирование зала" : "Новый зал"}</h3>
                            <p className="card-text">Кнопка редактирования схемы доступна только для уже созданного зала.</p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        {/* Чьё мероприятие — отдельный блок, чтобы поля партнёров не перемешивались
                            с ZIP, страной, городом и адресом. */}
                        <div
                            style={{
                                border: "1px solid var(--border-strong)",
                                borderRadius: "var(--radius-md)",
                                background: "var(--surface-muted)",
                                padding: 16,
                                display: "flex",
                                flexDirection: "column",
                                gap: 14,
                                marginBottom: 18,
                            }}
                        >
                            <h4 className="scheme-editor-section-title" style={{ marginTop: 0 }}>
                                Чьё мероприятие
                            </h4>

                            <Field label="Организатор" htmlFor="hall-owner">
                                <select
                                    id="hall-owner"
                                    className="ui-select"
                                    value={formState.owner}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, owner: event.target.value as HallOwner }))
                                    }
                                >
                                    {HALL_OWNER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            {formState.owner === "artist_production" && (
                                <Field label="ID сеанса Artist Production" htmlFor="hall-partner-seance-id">
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <Input
                                            id="hall-partner-seance-id"
                                            type="number"
                                            placeholder="Например, 12345"
                                            value={formState.partnerSeanceId}
                                            onChange={(event) =>
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    partnerSeanceId: event.target.value,
                                                }))
                                            }
                                        />
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleImportFromPartnerSeance}
                                            disabled={
                                                isImportingFromPartner ||
                                                !formState.partnerSeanceId.trim() ||
                                                !selectedId
                                            }
                                        >
                                            {isImportingFromPartner
                                                ? "Импортируем..."
                                                : "Импортировать схему из сеанса Artist Production"}
                                        </Button>
                                    </div>
                                    <span style={{ fontSize: 12, color: "#8a8f98", display: "block", marginTop: 4 }}>
                                        Кнопка ПОЛНОСТЬЮ ЗАМЕНЯЕТ схему (зоны и места) ТЕКУЩЕГО открытого зала данными сеанса (GET .../seances/{'{id}'}/hall). Название/адрес/город зала не трогает. Доступно только для уже сохранённого зала — сначала сохраните новый зал (название, город), затем импортируйте схему.
                                    </span>
                                </Field>
                            )}

                            {formState.owner === "nashe_de" && (
                                <Field label="ID мероприятия Nashe" htmlFor="hall-nashe-performance-id">
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                        <Input
                                            id="hall-nashe-performance-id"
                                            type="number"
                                            placeholder="Например, 345"
                                            value={formState.nashePerformanceId}
                                            onChange={(event) =>
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    nashePerformanceId: event.target.value,
                                                }))
                                            }
                                        />
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleLoadNashePerformances}
                                            disabled={isLoadingNashePerformances}
                                        >
                                            {isLoadingNashePerformances ? "Загружаем..." : "Список мероприятий Nashe"}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleImportFromNashe}
                                            disabled={isImportingFromNashe || !formState.nashePerformanceId.trim()}
                                        >
                                            {isImportingFromNashe
                                                ? "Импортируем..."
                                                : selectedId
                                                  ? "Импортировать схему из Nashe"
                                                  : "Создать зал из Nashe"}
                                        </Button>
                                    </div>
                                    {nashePerformances.length > 0 && (
                                        <select
                                            className="ui-select"
                                            style={{ marginTop: 8 }}
                                            value={formState.nashePerformanceId}
                                            onChange={(event) =>
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    nashePerformanceId: event.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Выберите мероприятие Nashe</option>
                                            {nashePerformances.map((performance) => (
                                                <option key={performance.id} value={String(performance.id)}>
                                                    {performance.id} — {performance.name}
                                                    {performance.venue?.name ? `, ${performance.venue.name}` : ""}
                                                    {performance.starts_at ? `, ${performance.starts_at.slice(0, 16).replace("T", " ")}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <span style={{ fontSize: 12, color: "#8a8f98", display: "block", marginTop: 4 }}>
                                        Для сохранённого зала кнопка ПОЛНОСТЬЮ ЗАМЕНЯЕТ его схему (категории и места) схемой зала мероприятия Nashe; название/адрес/город не трогает. Для нового (ещё не сохранённого) зала создаёт зал с названием и адресом площадки Nashe.
                                    </span>
                                </Field>
                            )}
                        </div>

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Название (RU)" htmlFor="hall-name-ru">
                                <Input
                                    id="hall-name-ru"
                                    value={formState.nameRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (EN)" htmlFor="hall-name-en">
                                <Input
                                    id="hall-name-en"
                                    value={formState.nameEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (DE)" htmlFor="hall-name-de">
                                <Input
                                    id="hall-name-de"
                                    value={formState.nameDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (UA)" htmlFor="hall-name-ua">
                                <Input
                                    id="hall-name-ua"
                                    value={formState.nameUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameUa: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field
                                label="Внутреннее название (только в панели)"
                                htmlFor="hall-internal-name"
                            >
                                <Input
                                    id="hall-internal-name"
                                    placeholder="Например: Название зала — 300 мест"
                                    value={formState.internalName}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, internalName: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="ZIP code" htmlFor="hall-zip-code">
                                <Input
                                    id="hall-zip-code"
                                    value={formState.zipCode}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, zipCode: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Страна" htmlFor="hall-country">
                                <select
                                    id="hall-country"
                                    className="ui-select"
                                    value={formState.countryId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            countryId: event.target.value,
                                            cityId: "",
                                        }))
                                    }
                                    disabled={isLoadingCountries}
                                >
                                    <option value="">
                                        {isLoadingCountries ? "Загрузка стран..." : "Выберите страну"}
                                    </option>

                                    {countries.map((country) => (
                                        <option key={country.id} value={country.id}>
                                            {country.name_ru}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Город" htmlFor="hall-city">
                                <select
                                    id="hall-city"
                                    className="ui-select"
                                    value={formState.cityId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            cityId: event.target.value,
                                        }))
                                    }
                                    disabled={!formState.countryId || isLoadingCities}
                                >
                                    <option value="">
                                        {!formState.countryId
                                            ? "Сначала выберите страну"
                                            : isLoadingCities
                                                ? "Загрузка городов..."
                                                : "Выберите город"}
                                    </option>

                                    {cities.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.name_ru}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Website" htmlFor="hall-website">
                                <Input
                                    id="hall-website"
                                    type="text"
                                    placeholder="https://example.com"
                                    value={formState.website}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, website: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Сортировка" htmlFor="hall-sort">
                                <Input
                                    id="hall-sort"
                                    type="number"
                                    value={formState.sort}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, sort: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Адрес (RU)" htmlFor="hall-address-ru">
                            <Input
                                id="hall-address-ru"
                                value={formState.addressRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, addressRu: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Адрес (EN)" htmlFor="hall-address-en">
                            <Input
                                id="hall-address-en"
                                value={formState.addressEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, addressEn: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Адрес (DE)" htmlFor="hall-address-de">
                            <Input
                                id="hall-address-de"
                                value={formState.addressDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, addressDe: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Адрес (UA)" htmlFor="hall-address-ua">
                            <Input
                                id="hall-address-ua"
                                value={formState.addressUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, addressUa: event.target.value }))
                                }
                            />
                        </Field>

                        <RichTextEditor
                            label="Описание (RU)"
                            value={formState.descriptionRu}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionRu: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (EN)"
                            value={formState.descriptionEn}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionEn: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (DE)"
                            value={formState.descriptionDe}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionDe: value }))
                            }
                        />

                        <RichTextEditor
                            label="Описание (UA)"
                            value={formState.descriptionUa}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, descriptionUa: value }))
                            }
                        />

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "Зал активен" : "Зал выключен"}
                            />
                        </div>

                        {selectedId ? (
                            <Card className="hall-gallery-card">
                                <div className="editor-head">
                                    <div>
                                        <h3 className="card-title">Фотогалерея зала</h3>
                                        <p className="card-text">
                                            Можно загрузить сразу несколько фотографий. Они отправятся по одной, затем появятся в списке ниже.
                                        </p>
                                    </div>
                                </div>

                                <div className="hall-gallery-upload">
                                    <div
                                        className="hall-gallery-dropzone"
                                        onDragEnter={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            event.dataTransfer.dropEffect = "copy";
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            void handleUploadHallPhotos(event.dataTransfer.files);
                                        }}
                                    >
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            className="hall-gallery-file-input"
                                            onChange={(event) => {
                                                void handleUploadHallPhotos(event.target.files);
                                                event.currentTarget.value = "";
                                            }}
                                        />
                                        <span className="hall-gallery-dropzone-title">
                                            Перетащите фото сюда или нажмите для выбора
                                        </span>
                                        <span className="hall-gallery-dropzone-text">
                                            Загрузка доступна только для уже созданного зала.
                                        </span>
                                    </div>
                                </div>

                                {isUploadingPhotos ? (
                                    <div className="form-global-success">Загружаем фотографии...</div>
                                ) : null}

                                {hallPhotos.length > 0 ? (
                                    <div className="hall-photos-grid">
                                        {hallPhotos.map((photoUrl, index) => (
                                            <div key={`${photoUrl}-${index}`} className="hall-photo-card">
                                                <img
                                                    src={photoUrl}
                                                    alt={`Hall photo ${index + 1}`}
                                                    className="hall-photo-preview"
                                                />

                                                <button
                                                    type="button"
                                                    className="hall-photo-delete"
                                                    onClick={() => {
                                                        void handleDeleteHallPhoto(photoUrl);
                                                    }}
                                                    disabled={isDeletingPhoto}
                                                >
                                                    ×
                                                </button>

                                                <div className="hall-photo-actions">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            void moveHallPhoto(index, index - 1);
                                                        }}
                                                        disabled={index === 0}
                                                    >
                                                        ↑
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            void moveHallPhoto(index, index + 1);
                                                        }}
                                                        disabled={index === hallPhotos.length - 1}
                                                    >
                                                        ↓
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-block">
                                        <h3 className="empty-block-title">Фотографий пока нет</h3>
                                        <p className="empty-block-text">
                                            После загрузки здесь появятся превью и кнопки сортировки/удаления.
                                        </p>
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}
                        {importWarnings.length > 0 ? (
                            <div className="form-global-error">
                                Предупреждения при импорте из Artist Production:
                                <ul>
                                    {importWarnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать зал"}
                            </Button>

                            <Button
                                type="button"
                                variant="ghost"
                                disabled={!selectedId || !formState.scheme}
                                onClick={() => {
                                    if (!selectedId || !formState.scheme) {
                                        return;
                                    }

                                    setIsSchemeEditorOpen(true);
                                }}
                            >
                                Редактировать схему
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>

            <HallSchemeEditorModal
                isOpen={isSchemeEditorOpen}
                scheme={formState.scheme}
                hallName={formState.nameRu}
                onClose={() => setIsSchemeEditorOpen(false)}
                onSave={(payload) => {
                    setFormState((prev) => ({
                        ...prev,
                        scheme: payload,
                    }));

                    setItems((prev) =>
                        prev.map((item) =>
                            item.id === selectedId
                                ? {
                                    ...item,
                                    scheme: payload,
                                }
                                : item,
                        ),
                    );
                }}
            />
        </div>
    );
};

export default HallsPage;
